import { useGetCurrentEscalation } from "@/lib/api/escalations";
import { useFindMentorship } from "@/lib/api/mentorships";
import { useIsRematched } from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { useIsChannelOnline } from "@/lib/hooks/conversations/use-is-channel-online";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  getLegalName,
  getMenteeDisplayPhoto,
  getPreferredName,
  isFieldRedacted,
} from "@/lib/redacted-display";
import { useChannelMentee } from "../use-channel-mentee";
import type { AttentionSurfaceCapabilities } from "./attention-capabilities";
import type {
  AttentionHeaderAction,
  AttentionHeaderProps,
} from "./attention-header";
import {
  computeAgeYears,
  resolveTeenHeaderBadges,
  resolveTeenSubtitleParts,
} from "./attention-header-content";
import { useAttentionAnalytics } from "./use-attention-analytics";
import type { UseAttentionAnalyticsResult } from "./use-attention-analytics";

/** Staff on a support channel are not a teen. Only a resolved mentorship mentee can be scheduled. */
export function resolveScheduleMenteeId(
  segment: string,
  mentorshipMenteeId: string | null | undefined,
): string | null {
  if (segment === "support" || !mentorshipMenteeId) return null;
  return mentorshipMenteeId;
}

export interface UseAttentionHeaderParams {
  channelUrl: string;
  segment: string;
  capabilities: AttentionSurfaceCapabilities;
  onCallTeen: () => void;
  onScheduleCall: () => void;
  onEscalate: () => void;
  onReport: () => void;
}

export interface UseAttentionHeaderResult {
  headerProps: AttentionHeaderProps;
  menteeId: string | null;
  scheduleMenteeId: string | null;
  isLoading: boolean;
  /** Single analytics instance bound to the resolved teen id, reused downstream. */
  analytics: UseAttentionAnalyticsResult;
}

export function useAttentionHeader({
  channelUrl,
  segment,
  capabilities,
  onCallTeen,
  onScheduleCall,
  onEscalate,
  onReport,
}: UseAttentionHeaderParams): UseAttentionHeaderResult {
  const ability = useAbility();

  const {
    channel,
    currentUserId,
    menteeId,
    name,
    avatarUrl: sendbirdAvatarUrl,
    isLoading: channelLoading,
  } = useChannelMentee(channelUrl);

  const analytics = useAttentionAnalytics(menteeId);
  const online = useIsChannelOnline(channel ?? undefined, currentUserId);

  const { data: mentorship, isLoading: mentorshipLoading } = useFindMentorship(
    currentUserId,
    menteeId ?? "",
  );
  const { data: isRematched } = useIsRematched(menteeId ?? "");
  const { data: escalation } = useGetCurrentEscalation(menteeId ?? undefined);
  const hasOpenEscalation = escalation?.status === "OPEN";

  const mentee = mentorship?.mentee;
  const rawAvatarUrl = mentee
    ? (getMenteeDisplayPhoto(mentee) ?? null)
    : sendbirdAvatarUrl;
  const { url: signedAvatarUrl } = useSignedMedia(rawAvatarUrl);
  const ageYears = isFieldRedacted("birthday", mentee?.redacted_fields)
    ? null
    : computeAgeYears(mentee?.birthday ?? null);
  const pronouns = isFieldRedacted("pronouns", mentee?.redacted_fields)
    ? null
    : (mentee?.pronouns ?? null);
  const gender = isFieldRedacted("sex", mentee?.profile?.redacted_fields)
    ? null
    : (mentee?.profile?.sex ?? null);

  const subtitleParts = resolveTeenSubtitleParts({
    ageYears,
    pronouns,
    gender,
  });

  const reactivation = mentorship?.metadata?.reactivation;
  const badges = resolveTeenHeaderBadges({
    reactivated: Boolean(reactivation) && reactivation?.banner_dismissed !== true,
    rematched: isRematched === true,
  });

  const scheduleMenteeId = resolveScheduleMenteeId(
    segment,
    mentorship?.mentee?.id,
  );
  const actions: AttentionHeaderAction[] = [];
  if (capabilities.canPerformHeaderActions) {
    if (menteeId && ability.can("create", "calls")) {
      actions.push({
        id: "call-teen",
        label: "Call teen",
        onSelect: () => {
          analytics.trackHeaderAction("call-teen");
          onCallTeen();
        },
      });
    }
    if (scheduleMenteeId && ability.can("create", "calendar-events")) {
      actions.push({
        id: "schedule-call",
        label: "Schedule call",
        onSelect: () => {
          analytics.trackHeaderAction("schedule-call");
          onScheduleCall();
        },
      });
    }
    if (ability.can("create", "escalations")) {
      actions.push({
        id: "escalate",
        label: hasOpenEscalation ? "View escalation" : "Escalate",
        destructive: hasOpenEscalation,
        onSelect: () => {
          analytics.trackHeaderAction("escalate");
          onEscalate();
        },
      });
    }
    if (ability.can("create", "risk-flagging")) {
      actions.push({
        id: "report",
        label: "Report",
        onSelect: () => {
          analytics.trackHeaderAction("report");
          onReport();
        },
        destructive: true,
      });
    }
  }

  return {
    headerProps: {
      name: (mentee ? getPreferredName(mentee) : "") || name,
      legalName: mentee ? getLegalName(mentee) : "",
      avatarUrl: signedAvatarUrl ?? null,
      online,
      subtitleParts,
      badges,
      actions,
    },
    menteeId,
    scheduleMenteeId,
    isLoading: channelLoading || mentorshipLoading,
    analytics,
  };
}
